import { useMemo, useCallback } from 'react';
import { format, addDays, startOfDay } from 'date-fns';
import type { ShiftListItemDto, CalendarEventDto as CalendarEventType } from '@signalvote/shared';
import { getCampaignTodayString, getDateStringInTimezone } from '@/lib/calendarUtils';
import { ShiftStatus } from '@signalvote/shared';
import { skip } from '@signalvote/data';
import { useShifts, useCalendarEvents, useKeyDates } from '@/hooks/queries';
import { groupKeyDatesByDate, type KeyDateItem } from '@/lib/keyDates';
import { useTaskStore } from '@/stores';

// --- Helpers ---

function parseShiftStatus(status: string): ShiftStatus {
  switch (status) {
    case 'Available':
      return ShiftStatus.Available;
    case 'Claimed':
      return ShiftStatus.Claimed;
    case 'InProgress':
      return ShiftStatus.InProgress;
    case 'Completed':
      return ShiftStatus.Completed;
    default:
      return ShiftStatus.Available;
  }
}

function addToBucket<T>(map: Map<string, T[]>, key: string, item: T): void {
  const bucket = map.get(key);
  if (bucket) {
    bucket.push(item);
  } else {
    map.set(key, [item]);
  }
}

function groupByDate<T>(items: T[], getDate: (item: T) => string): Map<string, T[]> {
  const map = new Map<string, T[]>();
  for (const item of items) {
    addToBucket(map, getDate(item), item);
  }
  return map;
}

// --- Hook ---

/**
 * Fetches and groups all calendar data (shifts, tasks, external events)
 * for a given date range. Used by both WeekView and MonthView.
 */
export function useCalendarData(startDate: Date | undefined, endDate: Date | undefined, timeZoneId?: string) {
  const dateRange = useMemo(() => {
    if (!startDate || !endDate) return null;
    return {
      startDate: format(startDate, 'yyyy-MM-dd'),
      endDate: format(endDate, 'yyyy-MM-dd'),
    };
  }, [startDate, endDate]);

  // Shifts
  const shiftsRead = useShifts(
    dateRange ? { startDate: dateRange.startDate, endDate: dateRange.endDate, pageSize: 100 } : skip
  );
  const shiftsResponse = shiftsRead.status === 'ready' ? shiftsRead.data : undefined;

  const shiftsByDate = useMemo(
    () => groupByDate(shiftsResponse?.items ?? [], (s) => s.date),
    [shiftsResponse]
  );

  const getShiftsForDate = useCallback(
    (dateStr: string): ShiftListItemDto[] => shiftsByDate.get(dateStr) || [],
    [shiftsByDate]
  );

  const getShiftStatus = useCallback(
    (id: string): ShiftStatus => {
      if (!shiftsResponse?.items) return ShiftStatus.Available;
      const shift = shiftsResponse.items.find((s) => s.id === id);
      return shift ? parseShiftStatus(shift.status) : ShiftStatus.Available;
    },
    [shiftsResponse]
  );

  const getClaimedBy = useCallback(
    (id: string): string | undefined => {
      if (!shiftsResponse?.items) return undefined;
      const shift = shiftsResponse.items.find((s) => s.id === id);
      return shift?.claimedByName || shift?.assignedToName || undefined;
    },
    [shiftsResponse]
  );

  // External calendar events
  // Add 1 day to endDate so the API range covers the full last visible day.
  // endDate is midnight local time of the last day; toISOString() converts to UTC
  // which can cut off events later that day (e.g., midnight CST = 6am UTC).
  const calendarEndDate = useMemo(
    () => endDate ? addDays(endDate, 1) : undefined,
    [endDate]
  );
  const eventsRead = useCalendarEvents(
    startDate && calendarEndDate ? { start: startDate.toISOString(), end: calendarEndDate.toISOString() } : skip
  );
  const calendarEventsResponse = eventsRead.status === 'ready' ? eventsRead.data : undefined;

  const { calendarEventsByDate, allDayEventsByDate } = useMemo(() => {
    const timed = new Map<string, CalendarEventType[]>();
    const allDay = new Map<string, CalendarEventType[]>();
    if (!calendarEventsResponse?.events) return { calendarEventsByDate: timed, allDayEventsByDate: allDay };

    for (const event of calendarEventsResponse.events) {
      if (event.isAllDay) {
        // All-day events span multiple days. Calendar providers use exclusive end dates
        // (e.g., a 3-day event Feb 3-5 has endTime of Feb 6), so we iterate up to but not including end.
        const start = startOfDay(new Date(event.startTime));
        const end = startOfDay(new Date(event.endTime));
        let current = start;
        while (current < end) {
          addToBucket(allDay, format(current, 'yyyy-MM-dd'), event);
          current = addDays(current, 1);
        }
      } else {
        addToBucket(timed, getDateStringInTimezone(event.startTime, timeZoneId), event);
      }
    }
    return { calendarEventsByDate: timed, allDayEventsByDate: allDay };
  }, [calendarEventsResponse, timeZoneId]);

  const getCalendarEventsForDate = useCallback(
    (dateStr: string): CalendarEventType[] => calendarEventsByDate.get(dateStr) || [],
    [calendarEventsByDate]
  );

  const getAllCalendarEventsForDate = useCallback(
    (dateStr: string): CalendarEventType[] => [
      ...(allDayEventsByDate.get(dateStr) || []),
      ...(calendarEventsByDate.get(dateStr) || []),
    ],
    [calendarEventsByDate, allDayEventsByDate]
  );

  const keyDatesRead = useKeyDates(undefined);
  const keyDatesResponse = keyDatesRead.status === 'ready' ? keyDatesRead.data : undefined;
  const keyDatesByDate = useMemo(
    () => groupKeyDatesByDate(keyDatesResponse?.keyDates ?? [], getCampaignTodayString()),
    [keyDatesResponse]
  );
  const getKeyDatesForDate = useCallback(
    (dateStr: string): KeyDateItem[] => keyDatesByDate.get(dateStr) || [],
    [keyDatesByDate]
  );

  // Tasks (from Zustand store)
  const getTasksForDate = useTaskStore((state) => state.getTasksForDate);

  return {
    shiftsRead,
    eventsRead,
    keyDatesRead,
    shiftsResponse,
    getShiftsForDate,
    getTasksForDate,
    getCalendarEventsForDate,
    getAllCalendarEventsForDate,
    allDayEventsByDate,
    keyDatesByDate,
    getKeyDatesForDate,
    getShiftStatus,
    getClaimedBy,
    parseShiftStatus,
  };
}
